import { useSignSmartWalletClaimQuery } from '@/__generated__/useSignSmartWalletClaimQuery.graphql'
import { DEFAULT_CHAIN } from '@/lib/chains'
import { walletClient } from '@/utils/wallet'
import { ChainId } from '@ourzora/chains'
import { ConnectedWallet } from '@privy-io/react-auth'
import { useMutation } from '@tanstack/react-query'
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay'
import {
  Address,
  ByteArray,
  encodeAbiParameters,
  encodePacked,
  hashTypedData,
  Hex,
  isAddressEqual,
  isHex,
  parseSignature,
  TypedData,
  TypedDataDefinition,
} from 'viem'

const query = graphql`
  query useSignSmartWalletClaimQuery($identifier: String!) {
    profile(identifier: $identifier) {
      ... on GraphQLAccountProfile {
        id
        smartWallet {
          smartWalletConfig {
            owners {
              ownerAddress
              ownerIndex
            }
          }
        }
      }
    }
  }
`

/**
 * Mutation for signing a claim transaction on behalf of the user's Smart Wallet
 */
export function useSignSmartWalletClaim() {
  const environment = useRelayEnvironment()

  return useMutation({
    mutationFn: async ({
      smartWalletAddress,
      signer,
      typedData,
      chainId,
    }: {
      smartWalletAddress: Address
      signer: ConnectedWallet
      typedData: TypedDataDefinition<TypedData, string>
      chainId: ChainId
    }) => {
      const { domain, types, primaryType, message } = typedData

      const messageHash = hashTypedData({
        domain,
        message,
        primaryType,
        types,
      })

      const replaySafeTypedData = toReplaySafeHash({
        address: smartWalletAddress,
        chainId,
        hash: messageHash,
      })

      // Sign the typed data directly instead of using signMessage
      const client = await walletClient({ wallet: signer, chain: DEFAULT_CHAIN })
      const signature = await client.signTypedData({
        ...replaySafeTypedData,
        account: signer.address as Address,
      })

      // Find the owner index of the smart wallet to submit the signature with
      const data = await fetchQuery<useSignSmartWalletClaimQuery>(environment, query, {
        identifier: signer.address,
      }).toPromise()
      if (!data?.profile?.smartWallet) {
        throw new Error('Smart wallet not found')
      }

      const ownerIndex = data.profile.smartWallet.smartWalletConfig?.owners?.find((o) =>
        isAddressEqual(o.ownerAddress, signer.address as Address)
      )?.ownerIndex

      return wrapSignature({
        ownerIndex,
        signature,
      })
    },
  })
}

/**
 * @description Retrieves the size of the value (in bytes).
 *
 * @param value The value (hex or byte array) to retrieve the size of.
 * @returns The size of the value (in bytes).
 */
function size(value: Hex | ByteArray) {
  if (isHex(value, { strict: false })) return Math.ceil((value.length - 2) / 2)
  return value.length
}

type CoinbaseSmartWalletTypedDataDefinition = TypedDataDefinition<
  {
    CoinbaseSmartWalletMessage: [{ name: 'hash'; type: 'bytes32' }]
  },
  'CoinbaseSmartWalletMessage'
>

function toReplaySafeHash({
  address,
  chainId,
  hash,
}: {
  address: Address
  chainId: number
  hash: Hex
}) {
  const typesAndMessage: Omit<CoinbaseSmartWalletTypedDataDefinition, 'domain'> = {
    types: {
      CoinbaseSmartWalletMessage: [
        {
          name: 'hash',
          type: 'bytes32',
        },
      ],
    },
    primaryType: 'CoinbaseSmartWalletMessage',
    message: {
      hash,
    },
  }

  return {
    ...typesAndMessage,
    domain: {
      chainId,
      name: 'Coinbase Smart Wallet',
      verifyingContract: address,
      version: '1',
    },
  }
}

function wrapSignature(parameters: { ownerIndex?: number | undefined; signature: Hex }) {
  const { ownerIndex = 0 } = parameters
  const signatureData = (() => {
    if (size(parameters.signature) !== 65) return parameters.signature
    const signature = parseSignature(parameters.signature)
    return encodePacked(
      ['bytes32', 'bytes32', 'uint8'],
      [signature.r, signature.s, signature.yParity === 0 ? 27 : 28]
    )
  })()
  return encodeAbiParameters(
    [
      {
        components: [
          {
            name: 'ownerIndex',
            type: 'uint8',
          },
          {
            name: 'signatureData',
            type: 'bytes',
          },
        ],
        type: 'tuple',
      },
    ],
    [
      {
        ownerIndex,
        signatureData,
      },
    ]
  )
}
